import React, { useState } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSessionTimeout } from '../../hooks/useSessionTimeout';
import SessionWarning from '../../components/SessionWarning';
import { 
  HomeIcon, 
  UsersIcon, 
  CalendarIcon, 
  PackageIcon, 
  PlusIcon, 
  CogIcon, 
  LogOutIcon,
  TicketIcon,
  GlobeIcon,
  ShoppingCartIcon,
  MailIcon,
  InfoIcon,
  MenuIcon,
  XIcon
} from 'lucide-react';
import bouncedadsLogo from '../../images/bouncedads1.png';
import DashboardHome from './DashboardHome';
import BookingsManagement from './BookingsManagement';
import ProductsManagement from './ProductsManagement';
import AddonsManagement from './AddonsManagement';
import CouponsManagement from './CouponsManagement';
import Settings from './Settings';
import UserManagement from './UserManagement';

const AdminDashboard: React.FC = () => {
  const { state } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { showWarning, countdown, isExpired, extendSession, handleLogout: sessionLogout } = useSessionTimeout();

  const handleLogout = () => {
    sessionLogout();
    navigate('/admin/login');
  };

  const allMenuItems = [
    { path: '/admin/dashboard', icon: HomeIcon, label: 'Dashboard', requiredRole: null, section: 'admin' },
    { path: '/admin/bookings', icon: CalendarIcon, label: 'Bookings', requiredRole: null, section: 'admin' },
    { path: '/admin/products', icon: PackageIcon, label: 'Products', requiredRole: null, section: 'admin' },
    { path: '/admin/addons', icon: PlusIcon, label: 'Addons', requiredRole: null, section: 'admin' },
    { path: '/admin/coupons', icon: TicketIcon, label: 'Coupons', requiredRole: null, section: 'admin' },
    { path: '/admin/users', icon: UsersIcon, label: 'Users', requiredRole: 'super_admin', section: 'admin' },
    { path: '/admin/settings', icon: CogIcon, label: 'Settings', requiredRole: null, section: 'admin' },
  ];

  const websitePages = [
    { path: '/home', icon: HomeIcon, label: 'Home', external: true },
    { path: '/rentals', icon: ShoppingCartIcon, label: 'Rentals', external: true },
    { path: '/contact', icon: MailIcon, label: 'Contact', external: true },
  ];

  // Filter menu items based on user role
  const menuItems = allMenuItems.filter(item => 
    !item.requiredRole || state.user?.role === item.requiredRole
  );

  const isActive = (path: string) => location.pathname === path || 
    (path !== '/admin/dashboard' && location.pathname.startsWith(path));

  return (
    <>
      <SessionWarning
        show={showWarning}
        countdown={countdown}
        isExpired={isExpired}
        onExtend={extendSession}
        onLogout={handleLogout}
      />
      <div className="min-h-screen bg-gray-100 flex">
        {/* Sidebar */}
        <div className={`fixed left-0 top-0 h-full bg-gray-900 text-white w-64 z-50 overflow-y-auto flex flex-col transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-2">
            <img 
              src={bouncedadsLogo} 
              alt="Bounce Dads Logo" 
              className="h-10 w-10 object-contain rounded"
            />
            <h1 className="text-2xl font-bold">Bounce Dads</h1>
          </div>
          <p className="text-gray-400 text-sm">Admin Panel</p>
        </div>
        
        <nav className="flex-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center px-6 py-3 text-sm font-medium transition-colors ${
                isActive(item.path)
                  ? 'bg-blue-600 text-white border-r-4 border-blue-400'
                  : 'text-gray-300 hover:bg-gray-800 hover:text-white'
              }`}
            >
              <item.icon className="h-5 w-5 mr-3" />
              {item.label}
            </Link>
          ))}
          
          {/* Website Pages Section */}
          <div className="mt-8 px-6">
            <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Website Pages
            </h3>
          </div>
          {websitePages.map((page) => (
            <a
              key={page.path}
              href={page.path}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center px-6 py-3 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
            >
              <page.icon className="h-5 w-5 mr-3" />
              {page.label}
            </a>
          ))}
        </nav>
        
        <div className="p-6">
          <button
            onClick={handleLogout}
            className="flex items-center w-full px-6 py-3 text-sm font-medium text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOutIcon className="h-5 w-5 mr-3" />
            Sign Out
          </button>
        </div>
      </div>
      
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      {/* min-w-0 is required: as a flex item this column defaults to
          min-width:auto, so it refuses to shrink below its widest child and any
          wide table pushes it past the viewport. mobile.css hides overflow on
          the body, so that showed up as content silently cut off the right edge
          rather than a scrollbar. With min-w-0 the column tracks the viewport
          and the tables' own overflow-x-auto wrappers do the scrolling. */}
      <div className="flex-1 min-w-0 ml-0 lg:ml-64">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="px-4 py-4 sm:px-6">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center space-x-4 min-w-0">
                {/* Mobile menu toggle */}
                <button
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                >
                  {sidebarOpen ? <XIcon className="h-6 w-6" /> : <MenuIcon className="h-6 w-6" />}
                </button>
                <h2 className="text-xl font-semibold text-gray-800 truncate">
                  {menuItems.find(item => isActive(item.path))?.label || 'Dashboard'}
                </h2>
              </div>
              <div className="flex items-center space-x-3 min-w-0">
                <div className="text-right min-w-0">
                  <span className="block truncate text-sm font-medium text-gray-900">{state.user?.username || 'Admin User'}</span>
                  <span className="block text-xs text-gray-500 capitalize">{state.user?.role || 'admin'}</span>
                </div>
                <div className="h-8 w-8 flex-shrink-0 bg-blue-600 rounded-full flex items-center justify-center">
                  <UsersIcon className="h-4 w-4 text-white" />
                </div>
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-6">
          <Routes>
            <Route path="/" element={<DashboardHome />} />
            <Route path="/bookings" element={<BookingsManagement />} />
            <Route path="/products" element={<ProductsManagement />} />
            <Route path="/addons" element={<AddonsManagement />} />
            <Route path="/coupons" element={<CouponsManagement />} />
            <Route 
              path="/users" 
              element={
                state.user?.role === 'super_admin' ? 
                <UserManagement /> : 
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <h3 className="text-red-800 font-medium">Access Denied</h3>
                  <p className="text-red-600 text-sm mt-1">You don't have permission to access user management.</p>
                </div>
              } 
            />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </main>
      </div>
    </div>
    </>
  );
};

export default AdminDashboard;
